// CommandPalette — global ⌘K / Ctrl+K search & jump.
// Opens on hotkey or via header button. Filters pages, actions and people.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ArrowRight, LayoutDashboard, CalendarDays, Users, MapPin,
  Building2, ClipboardList, Repeat, FileBarChart, FolderOpen, BookOpen,
  CreditCard, Coffee, Settings as SettingsIcon, Wallet, Megaphone, Bell,
  User as UserIcon, LogOut, Plus, Zap, Sparkles, CornerDownLeft,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";

const MANAGER_ROLES = ["owner", "manager", "teamlead"];

function buildPages(t, role) {
  const isOwner = role === "owner";
  const isMgmt = MANAGER_ROLES.includes(role);
  const pages = [];
  if (isMgmt) {
    pages.push(
      { to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard },
      { to: "/roster", label: t("roster"), icon: CalendarDays },
      { to: "/employees", label: t("employees"), icon: Users },
      { to: "/teams", label: t("teams"), icon: Building2 },
      { to: "/requests", label: t("leave_requests"), icon: ClipboardList },
      { to: "/swaps", label: t("swap_requests"), icon: Repeat },
      { to: "/timesheets", label: t("timesheets") || "Timesheets", icon: ClipboardList },
      { to: "/reports", label: t("reports_label"), icon: FileBarChart },
    );
    if (isOwner || role === "manager") {
      pages.push({ to: "/payroll", label: t("payroll_label"), icon: Wallet });
    }
    if (isOwner) {
      pages.push(
        { to: "/locations", label: t("locations"), icon: MapPin },
        { to: "/billing", label: t("billing_label"), icon: CreditCard },
        { to: "/settings", label: t("settings"), icon: SettingsIcon },
      );
    }
    if (isOwner || role === "manager") {
      pages.push({ to: "/pause-settings", label: t("pause_nav_label"), icon: Coffee });
    }
  } else {
    pages.push(
      { to: "/my-roster", label: t("my_roster"), icon: CalendarDays },
      { to: "/my-overview", label: t("my_overview") || "Mijn overzicht", icon: LayoutDashboard },
      { to: "/my-leave", label: t("my_leave"), icon: ClipboardList },
      { to: "/my-swaps", label: t("my_swaps"), icon: Repeat },
      { to: "/my-availability", label: t("my_availability") || "Beschikbaarheid", icon: CalendarDays },
    );
  }
  pages.push(
    { to: "/documents", label: t("documents_label"), icon: FolderOpen },
    { to: "/news", label: t("news"), icon: Megaphone },
    { to: "/notifications", label: t("notifications"), icon: Bell },
    { to: "/handbook", label: t("handbook"), icon: BookOpen },
    { to: "/profile", label: t("profile") || "Profiel", icon: UserIcon },
  );
  return pages;
}

function buildActions(t, role, navigate, logout) {
  const items = [];
  if (MANAGER_ROLES.includes(role)) {
    items.push(
      { label: t("cmdk_action_new_shift") || "Nieuwe dienst", icon: Plus, run: () => navigate("/roster?new=1") },
      { label: t("cmdk_action_quick_assign") || "Snel toewijzen", icon: Zap, run: () => navigate("/quick-assign") },
    );
  }
  items.push(
    { label: t("cmdk_action_whats_new") || "Wat is er nieuw", icon: Sparkles, run: () => {
      try { localStorage.removeItem("rostr.whatsnew_seen"); } catch {}
      window.dispatchEvent(new Event("rostr:show-whats-new"));
    }},
    { label: t("cmdk_action_toggle_theme") || "Wissel thema", icon: Sparkles, run: () => {
      const isDark = document.documentElement.classList.toggle("dark");
      localStorage.setItem("rooster.theme", isDark ? "dark" : "light");
    }},
    { label: t("logout"), icon: LogOut, run: logout, danger: true },
  );
  return items;
}

function useHotkey(onOpen) {
  useEffect(() => {
    function onKey(e) {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpen();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpen]);
}

export default function CommandPalette({ open, onClose }) {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const isMgmt = MANAGER_ROLES.includes(user?.role);

  // Fetch people once for managers (skip for employees).
  useEffect(() => {
    if (!open || !isMgmt || people.length > 0) return;
    api.get("/users").then((r) => setPeople(r.data || [])).catch(() => {});
  }, [open, isMgmt]); // eslint-disable-line

  // Reset on open, focus input.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const pages = useMemo(() => buildPages(t, user?.role), [t, user?.role]);
  const actions = useMemo(
    () => buildActions(t, user?.role, (to) => { navigate(to); onClose(); }, () => { logout(); onClose(); }),
    [t, user?.role, navigate, logout, onClose],
  );

  const q = query.trim().toLowerCase();
  const match = (label) => !q || label.toLowerCase().includes(q);

  const filteredPages = pages.filter((p) => match(p.label));
  const filteredActions = actions.filter((a) => match(a.label));
  const filteredPeople = isMgmt
    ? people.filter((p) => match(p.name || "") || match(p.email || "")).slice(0, 8)
    : [];

  // Flat list for keyboard navigation
  const flat = [
    ...filteredPages.map((p) => ({ type: "page", ...p })),
    ...filteredActions.map((a) => ({ type: "action", ...a })),
    ...filteredPeople.map((p) => ({ type: "person", ...p })),
  ];

  useEffect(() => { setActiveIdx(0); }, [query]);

  const runItem = (item) => {
    if (!item) return;
    if (item.type === "page") { navigate(item.to); onClose(); }
    else if (item.type === "action") { item.run?.(); onClose(); }
    else if (item.type === "person") { navigate(`/employees?open=${encodeURIComponent(item._id)}`); onClose(); }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); runItem(flat[activeIdx]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  // Scroll active item into view on move
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmdk-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const sectionLabel = (key, fallback) => t(key) || fallback;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[60] bg-black/45 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
          onClick={onClose}
          data-testid="command-palette"
        >
          <motion.div
            initial={{ scale: 0.96, y: -8, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: -8, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
            className="w-full max-w-[640px] rounded-2xl bg-[hsl(var(--surface))] border hairline overflow-hidden shadow-[var(--shadow-xl)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("cmdk_placeholder")}
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-4 h-14 border-b hairline">
              <Search size={17} strokeWidth={2} className="text-[hsl(var(--text-muted))]" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("cmdk_placeholder")}
                className="flex-1 bg-transparent outline-none text-[15px] text-[hsl(var(--text-strong))] placeholder:text-[hsl(var(--text-muted))]"
                data-testid="cmdk-input"
              />
              <kbd className="hidden sm:inline-flex items-center gap-0.5 h-6 px-1.5 rounded-md border hairline bg-[hsl(var(--bg))] text-[10px] font-mono text-[hsl(var(--text-muted))]">
                Esc
              </kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2" data-testid="cmdk-list">
              {flat.length === 0 ? (
                <div className="px-4 py-10 text-center text-[13px] text-[hsl(var(--text-muted))]" data-testid="cmdk-empty">
                  {sectionLabel("cmdk_empty", "Geen resultaten")}
                </div>
              ) : (
                <>
                  {filteredPages.length > 0 && (
                    <Section title={sectionLabel("cmdk_section_pages", "Pagina's")}>
                      {filteredPages.map((p, i) => (
                        <Item
                          key={`p-${p.to}`}
                          icon={p.icon}
                          label={p.label}
                          hint={p.to}
                          active={activeIdx === i}
                          idx={i}
                          onMouseEnter={() => setActiveIdx(i)}
                          onClick={() => runItem({ type: "page", ...p })}
                          testid={`cmdk-page-${p.to.replace(/\//g, "")}`}
                        />
                      ))}
                    </Section>
                  )}
                  {filteredActions.length > 0 && (
                    <Section title={sectionLabel("cmdk_section_actions", "Acties")}>
                      {filteredActions.map((a, i) => {
                        const idx = filteredPages.length + i;
                        return (
                          <Item
                            key={`a-${a.label}`}
                            icon={a.icon}
                            label={a.label}
                            active={activeIdx === idx}
                            idx={idx}
                            danger={a.danger}
                            onMouseEnter={() => setActiveIdx(idx)}
                            onClick={() => runItem({ type: "action", ...a })}
                            testid={`cmdk-action-${i}`}
                          />
                        );
                      })}
                    </Section>
                  )}
                  {filteredPeople.length > 0 && (
                    <Section title={sectionLabel("cmdk_section_people", "Medewerkers")}>
                      {filteredPeople.map((p, i) => {
                        const idx = filteredPages.length + filteredActions.length + i;
                        return (
                          <Item
                            key={`u-${p._id}`}
                            avatar={(p.name || "?").slice(0, 1).toUpperCase()}
                            label={p.name || p.email}
                            hint={p.email}
                            active={activeIdx === idx}
                            idx={idx}
                            onMouseEnter={() => setActiveIdx(idx)}
                            onClick={() => runItem({ type: "person", ...p })}
                            testid={`cmdk-person-${p._id}`}
                          />
                        );
                      })}
                    </Section>
                  )}
                </>
              )}
            </div>

            {/* Footer hint */}
            <div className="flex items-center justify-between px-4 h-10 border-t hairline bg-[hsl(var(--bg))] text-[11px] text-[hsl(var(--text-muted))]">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <kbd className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded border hairline bg-[hsl(var(--surface))] font-mono">↑</kbd>
                  <kbd className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded border hairline bg-[hsl(var(--surface))] font-mono">↓</kbd>
                  {sectionLabel("cmdk_hint_navigate", "navigeer")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <CornerDownLeft size={11} strokeWidth={2.2} />
                  {sectionLabel("cmdk_hint_open", "openen")}
                </span>
              </div>
              <span className="inline-flex items-center gap-1 tabular-nums">
                {flat.length} {sectionLabel("cmdk_hint_results", "resultaten")}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-1">
      <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Item({ icon: Icon, avatar, label, hint, active, idx, onMouseEnter, onClick, danger, testid }) {
  return (
    <button
      type="button"
      data-cmdk-idx={idx}
      data-testid={testid}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        active ? "bg-[hsl(var(--primary-soft))]" : "hover:bg-[hsl(var(--muted))]"
      }`}
    >
      {Icon ? (
        <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${
          active ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"
        } ${danger && !active ? "!text-[hsl(var(--danger))]" : ""}`}>
          <Icon size={14} strokeWidth={2} />
        </span>
      ) : (
        <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(217_91%_45%)] text-white text-[11px] font-bold">
          {avatar}
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className={`block text-[13.5px] font-semibold truncate ${
          active ? "text-[hsl(var(--primary))]" : danger ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--text-strong))]"
        }`}>{label}</span>
        {hint && <span className="block text-[11px] text-[hsl(var(--text-muted))] truncate">{hint}</span>}
      </span>
      {active && <ArrowRight size={13} strokeWidth={2.2} className="text-[hsl(var(--primary))] shrink-0" />}
    </button>
  );
}

export { useHotkey };
